<script lang="ts" setup>
import SideBarMenu from '@components/SideBar/SideBarMenu.vue'
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useResponsive } from '@/composables/useResponsive'
import { useSettingsLogo } from '@/settings'
import { useMenuChildrenStore } from '@/stores/menuChildrenStore'
import { useMenuStore } from '@/stores/menuStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useSidebarStore } from '@/stores/sidebar.store'

const HeaderToolbarUser = defineAsyncComponent(() => import('@components/Header/HeaderToolbarUser.vue'))
const SideBarExpanded = defineAsyncComponent(() => import('@components/SideBar/SideBarExpanded.vue'))

const route = useRoute()
const menuStore = useMenuStore()
const sidebarStore = useSidebarStore()
const settingsStore = useSettingsStore()
const menuChildrenStore = useMenuChildrenStore()
const settingsLogo = useSettingsLogo()
const { isMobile } = useResponsive()

const organizationName = computed(() => settingsStore.organizationName)
const logo = computed(() => settingsLogo.logo)

const closeSubmenu = () => {
  menuChildrenStore.setIsLocked(false)
  menuChildrenStore.updateMenuStore()
}

const closeNavigation = () => {
  closeSubmenu()

  if (isMobile.value) {
    sidebarStore.close()
  }
}

const onClickOutside = (evt: MouseEvent | TouchEvent) => {
  const target = evt?.target as HTMLElement

  // Элементы с этим атрибутом самостоятельно управляют состоянием меню.
  if (target && target.closest('[data-prevent-hide-menu]')) return false

  closeNavigation()
}

const onMouseLeave = () => {
  if (!menuChildrenStore.getIsLocked) {
    menuChildrenStore.updateMenuStore()
  }
}

const onKeydown = (evt: KeyboardEvent) => {
  if (evt.key !== 'Escape') return

  closeNavigation()
}

watch(route, () => {
  menuStore.setActiveItemFromRoute(route)

  if (isMobile.value) {
    closeNavigation()
  }
})

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div
    v-if="isMobile && sidebarStore.getIsVisible"
    class="sidebar-overlay"
    aria-hidden="true"
    @click="closeNavigation"
  />

  <aside
    v-show="sidebarStore.getIsVisible"
    v-click-outside="onClickOutside"
    class="sidebar"
    aria-label="Основная навигация"
    @mouseleave="onMouseLeave"
  >
    <VCard
      class="sidebar__card"
      elevation="0"
    >
      <RouterLink
        to="/"
        class="sidebar__brand"
        title="Перейти на главную страницу"
        @click="closeNavigation"
      >
        <img
          v-if="logo"
          class="sidebar__logo"
          :src="logo"
          alt=""
        >
      </RouterLink>

      <HeaderToolbarUser
        v-if="isMobile"
        class="sidebar__mobile-user"
        mode="sidebar"
      />

      <div class="sidebar__menu">
        <SideBarMenu />
      </div>

      <div
        v-if="organizationName"
        class="sidebar__footer"
      >
        <span class="sidebar__footer-label">Организация</span>
        <strong :title="organizationName">{{ organizationName }}</strong>
      </div>
    </VCard>

    <SideBarExpanded />
  </aside>
</template>

<style lang="scss">
@use '@/styles/variables.scss' as vars;

.sidebar-overlay {
  position: fixed;
  inset: 0;
  z-index: 29;
  background: rgb(4 19 17 / 48%);
  backdrop-filter: blur(2px);
}

.sidebar {
  display: flex;
  flex-shrink: 0;
  position: sticky;
  top: vars.$app-padding;
  width: var(--ui-sidebar-width);
  height: calc(100vh - (vars.$app-padding * 2));
  border-radius: var(--ui-radius-lg);
  color: var(--ui-menu-text);
  background: var(--ui-menu-background);
  box-shadow: var(--ui-shadow-md);
  isolation: isolate;
  z-index: 30;

  .sidebar__card {
    display: flex;
    flex-direction: column;
    padding: 20px 14px 16px;
    width: 100%;
    height: 100%;
    color: inherit;
    background: var(--ui-menu-background);
    border-radius: inherit;
    overflow: hidden;
    z-index: 3;
  }

  .sidebar__brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    min-height: 48px;
    padding: 4px 8px 18px;
    color: inherit;
    border-bottom: 1px solid currentColor;
  }

  .sidebar__logo {
    flex: 0 0 auto;
    width: auto;
    height: 38px;
    object-fit: contain;
  }

  .sidebar__brand-title {
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 750;
    line-height: 1.25;
    text-overflow: ellipsis;
  }

  .sidebar__menu {
    flex: 1 1 auto;
    min-height: 0;
    padding: 14px 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .sidebar__footer {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 10px 2px;
    border-top: 1px solid currentColor;

    strong {
      overflow: hidden;
      font-size: 0.8125rem;
      line-height: 1.25;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .sidebar__footer-label {
    font-size: 0.6875rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.7;
  }
}

@media (max-width: 959px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: min(320px, calc(100vw - 32px));
    height: 100vh;
    border-radius: 0 var(--ui-radius-lg) var(--ui-radius-lg) 0;
    z-index: 30;

    .sidebar__card {
      padding-top: 14px;
    }

    .sidebar__mobile-user {
      margin-top: 12px;
    }
  }
}
</style>
